import axios from 'axios';
import { clearSessionCookie } from '../utils/sessionCookie';
import { installPermissionInterceptor } from '../olli/components/PermissionNotice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// A staff member without a module gets 403 PERMISSION_DENIED: one notice for the whole panel.
installPermissionInterceptor(api);

// Handle token expiration
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Only redirect to login if 401 and NOT from login endpoint
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearSessionCookie();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    checkEmail: (email) => api.post('/auth/check-email', { email }),
    sendOTP: (data) => api.post('/auth/send-otp', data),
    verifyOTP: (data) => api.post('/auth/verify-otp', data),
    resendOTP: (data) => api.post('/auth/resend-otp', data),
    register: (userData) => api.post('/auth/register', userData),
    verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
    resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
    getCurrentUser: () => api.get('/auth/me'),
};

// Organizations APIs
export const organizationsAPI = {
    getMyOrganizations: () => api.get('/organizations/my-organizations'),
    getOrganizationById: (id) => api.get(`/organizations/${id}`),
    acceptInvitation: (token) => api.post('/organizations/accept-invitation', { token }),
};

// Users APIs
export const usersAPI = {
    getProfile: () => api.get('/users/profile'),
    updateProfile: (data) => api.put('/users/profile', data),
    changePassword: (data) => api.put('/users/change-password', data),
};

// WABA Wallet APIs
export const walletAPI = {
    getWallet: () => api.get('/waba/wallet'),
    getTransactions: (params) => api.get('/waba/wallet/transactions', { params }),
    getSummary: (period) => api.get('/waba/wallet/summary', { params: { period } }),
    updateSettings: (data) => api.put('/waba/wallet/settings', data),
};

// WABA Pricing APIs
export const pricingAPI = {
    getPricing: () => api.get('/waba/pricing'),
    calculateCost: (data) => api.post('/waba/pricing/calculate', data),
};

// Payment APIs
export const paymentAPI = {
    calculateGst: (amount) => api.get(`/payments/calculate-gst?amount=${amount}`),
    initiatePayment: (amount) => api.post('/payments/initiate', { amount }),
    verifyRazorpayPayment: (data) => api.post('/payments/razorpay/verify', data),
    getStatus: (txnid) => api.get(`/payments/status/${txnid}`),
    getHistory: (params) => api.get('/payments/history', { params }),
};

// Contact Tags APIs
export const contactTagsAPI = {
    // Tag CRUD
    getAllTags: () => api.get('/contacts/tags'),
    createTag: (data) => api.post('/contacts/tags', data),
    updateTag: (id, data) => api.put(`/contacts/tags/${id}`, data),
    deleteTag: (id) => api.delete(`/contacts/tags/${id}`),

    // Contact-Tag operations
    getContactTags: (contactId) => api.get(`/contacts/${contactId}/tags`),
    addTagsToContact: (contactId, tagIds) => api.post(`/contacts/${contactId}/tags`, { tag_ids: tagIds }),
    setContactTags: (contactId, tagIds) => api.put(`/contacts/${contactId}/tags`, { tag_ids: tagIds }),
    removeTagFromContact: (contactId, tagId) => api.delete(`/contacts/${contactId}/tags/${tagId}`),
};

// Contacts APIs
export const contactsAPI = {
    getContacts: (params) => api.get('/contacts', { params }),
    getContact: (id) => api.get(`/contacts/${id}`),
    createContact: (data) => api.post('/contacts', data),
    updateContact: (id, data) => api.put(`/contacts/${id}`, data),
    deleteContact: (id) => api.delete(`/contacts/${id}`),
};

// Contact Import APIs
export const contactImportAPI = {
    downloadTemplate: () => api.get('/contacts/import/template', { responseType: 'blob' }),
    previewImport: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/contacts/import/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    importContacts: (file, duplicateAction) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('duplicate_action', duplicateAction);
        return api.post('/contacts/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }
};

// Complaint APIs
export const complaintAPI = {
    // Complaints
    getComplaints: (params) => api.get('/complaints', { params }),
    getComplaint: (id) => api.get(`/complaints/${id}`),
    createComplaint: (data) => api.post('/complaints', data),
    updateComplaint: (id, data) => api.put(`/complaints/${id}`, data),
    deleteComplaint: (id) => api.delete(`/complaints/${id}`),
    addReply: (id, data) => api.post(`/complaints/${id}/reply`, data),
    getStats: (params) => api.get('/complaints/stats', { params }),

    // Categories
    getCategories: (params) => api.get('/complaint-categories', { params }),
    getCategory: (id) => api.get(`/complaint-categories/${id}`),
    createCategory: (data) => api.post('/complaint-categories', data),
    updateCategory: (id, data) => api.put(`/complaint-categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/complaint-categories/${id}`),
};

// Feedback APIs
export const feedbackAPI = {
    // Feedbacks
    getFeedbacks: (params) => api.get('/feedbacks', { params }),
    getFeedback: (id) => api.get(`/feedbacks/${id}`),
    createFeedback: (data) => api.post('/feedbacks', data),
    updateFeedback: (id, data) => api.put(`/feedbacks/${id}`, data),
    deleteFeedback: (id) => api.delete(`/feedbacks/${id}`),
    getStats: (params) => api.get('/feedbacks/stats', { params }),

    // Categories
    getCategories: (params) => api.get('/feedback-categories', { params }),
    getCategory: (id) => api.get(`/feedback-categories/${id}`),
    createCategory: (data) => api.post('/feedback-categories', data),
    updateCategory: (id, data) => api.put(`/feedback-categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/feedback-categories/${id}`),
};

// Knowledge Base APIs
export const knowledgeBaseAPI = {
    // Stats (Super Admin)
    getStats: () => api.get('/kb/stats'),

    // Categories
    getCategories: () => api.get('/kb/categories'),
    createCategory: (data) => api.post('/kb/categories', data),
    updateCategory: (id, data) => api.put(`/kb/categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/kb/categories/${id}`),

    // Documents (Super Admin)
    getDocuments: (params) => api.get('/kb/documents', { params }),
    getDocument: (id) => api.get(`/kb/documents/${id}`),
    getDocumentStatus: (id) => api.get(`/kb/documents/${id}/status`),
    uploadDocument: (file, categoryId, facilityId) => {
        const formData = new FormData();
        formData.append('file', file);
        if (categoryId) {
            formData.append('category_id', categoryId);
        }
        if (facilityId) {
            formData.append('facility_id', facilityId);
        }
        return api.post('/kb/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    processDocuments: (documentIds) => api.post('/kb/documents/process', { document_ids: documentIds }),
    getQueueStatus: () => api.get('/kb/documents/queue/status'),
    deleteDocument: (id) => api.delete(`/kb/documents/${id}`),

    // Conversations (All Users)
    getConversations: (params) => api.get('/kb/conversations', { params }),
    createConversation: (title) => api.post('/kb/conversations', { title }),
    getConversation: (id) => api.get(`/kb/conversations/${id}`),
    deleteConversation: (id) => api.delete(`/kb/conversations/${id}`),

    // Chat
    sendMessage: (data) => api.post('/kb/chat', data),
};

// AI Agent APIs (Org-level configuration, multi-agent)
export const aiAgentAPI = {
    // Config CRUD
    getConfigs: () => api.get('/ai-agent/config'),
    createConfig: (data) => api.post('/ai-agent/config', data),
    updateConfig: (id, data) => api.put(`/ai-agent/config/${id}`, data),
    deleteConfig: (id) => api.delete(`/ai-agent/config/${id}`),

    // Per-channel language overrides. Empty array clears all overrides;
    // any channel without an override falls back to the org-wide
    // supported_language_codes + default_language_code on the config row.
    getChannelLanguages: (id) => api.get(`/ai-agent/config/${id}/channel-languages`),
    setChannelLanguages: (id, overrides) => api.put(`/ai-agent/config/${id}/channel-languages`, { overrides }),

    // Agent actions
    regenerateApiKey: (configId) => api.post('/ai-agent/regenerate-key', { config_id: configId }),
    toggleActive: (configId, isActive) => api.post('/ai-agent/toggle', { config_id: configId, is_active: isActive }),

    // Test chat
    testChat: (configId, data) => api.post(`/ai-agent/config/${configId}/test`, data),
    testVoiceChat: (configId, formData) => api.post(`/ai-agent/config/${configId}/test-voice`, formData, {
        headers: { 'Content-Type': undefined }
    }),

    // Templates
    getTemplates: (params) => api.get('/ai-agent/templates', { params }),

    // WABA configs (for WhatsApp number linking)
    getWabaConfigs: () => api.get('/ai-agent/waba-configs'),

    // Notification config helpers (wizard "Notifications" step)
    getNotificationVars: () => api.get('/ai-agent/notification-vars'),
    getApprovedTemplates: (wabaConfigId) => api.get('/ai-agent/approved-templates', {
        params: wabaConfigId ? { waba_config_id: wabaConfigId } : {}
    }),

    // Canonical specialties list for the wizard's specialty picker. Each entry
    // is annotated with at_org=true if the org has a doctor of that specialty.
    getSpecialties: () => api.get('/ai-agent/specialties'),

    // Conversations & Analytics
    getConversations: (params) => api.get('/ai-agent/conversations', { params }),
    getConversationMessages: (id) => api.get(`/ai-agent/conversations/${id}/messages`),
    getAnalytics: (period) => api.get('/ai-agent/analytics', { params: { period } }),

    // TTS voices (Cartesia by default; Gemini Live when engine=voice_live)
    getVoices: (engine) => api.get('/ai-agent/voices', engine ? { params: { engine } } : undefined),

    // Voice preview
    ttsPreview: (voice, speed = 1.0, configId = null, language = null) => {
        const data = { voice, speed };
        if (configId) data.config_id = configId;
        if (language) data.language = language;
        return api.post('/ai-agent/tts-preview', data, { responseType: 'blob' });
    },

    // Widget
    getApiKey: (configId) => api.get(`/ai-agent/config/${configId}/api-key`),
    updateWidgetConfig: (configId, widgetConfig) => api.put(`/ai-agent/config/${configId}`, { widget_config: widgetConfig }),
    updateVoiceWidgetConfig: (configId, voiceWidgetConfig) => api.put(`/ai-agent/config/${configId}`, { voice_widget_config: voiceWidgetConfig }),
    uploadWidgetAsset: (configId, type, file) => {
        const formData = new FormData();
        formData.append('image', file);
        return api.post(`/ai-agent/config/${configId}/widget-upload?type=${type}`, formData, {
            headers: { 'Content-Type': undefined }
        });
    },

    // Phone Numbers (IVR) — attach/detach against admin-procured pool
    getPhoneNumbers: () => api.get('/ai-agent/phone-numbers'),
    getAvailablePoolNumbers: () => api.get('/ai-agent/phone-numbers/pool'),
    attachPhoneNumber: (id, data) => api.post(`/ai-agent/phone-numbers/${id}/attach`, data),
    detachPhoneNumber: (id) => api.post(`/ai-agent/phone-numbers/${id}/detach`),
    updatePhoneNumber: (id, data) => api.put(`/ai-agent/phone-numbers/${id}`, data),
    // Outbound caller-ID assignments
    getOutboundAgents: (phoneId) => api.get(`/ai-agent/phone-numbers/${phoneId}/outbound-agents`),
    attachOutboundAgent: (phoneId, data) => api.post(`/ai-agent/phone-numbers/${phoneId}/outbound-agents`, data),
    detachOutboundAgent: (phoneId, agentConfigId) => api.delete(`/ai-agent/phone-numbers/${phoneId}/outbound-agents/${agentConfigId}`)
};

// Facility Departments & Duty Roster APIs
export const facilityDepartmentsAPI = {
    // Departments
    getDepartments: (params) => api.get('/facility-departments', { params }),
    createDepartment: (data) => api.post('/facility-departments', data),
    updateDepartment: (id, data) => api.put(`/facility-departments/${id}`, data),
    deleteDepartment: (id) => api.delete(`/facility-departments/${id}`),
    // Duty Rosters
    getRoster: (departmentId, params) => api.get(`/facility-departments/${departmentId}/roster`, { params }),
    createRosterEntry: (departmentId, data) => api.post(`/facility-departments/${departmentId}/roster`, data),
    updateRosterEntry: (id, data) => api.put(`/facility-departments/roster/${id}`, data),
    deleteRosterEntry: (id) => api.delete(`/facility-departments/roster/${id}`),
};

// Leads APIs
export const leadsAPI = {
    createLead: (data) => api.post('/leads', data),
};

// Patients APIs
export const patientsAPI = {
    createPatient: (data) => api.post('/patients', data),
};

// WABA Conversation APIs
export const wabaConversationAPI = {
    updateConversation: (id, data) => api.put(`/waba/conversations/${id}`, data),
};

// Treatment Management APIs
export const treatmentAPI = {
    // Treatment Types
    getTypes: (params) => api.get('/treatments', { params }),
    getType: (id) => api.get(`/treatments/${id}`),
    createType: (data) => api.post('/treatments', data),
    updateType: (id, data) => api.put(`/treatments/${id}`, data),
    deleteType: (id) => api.delete(`/treatments/${id}`),
    // Statuses
    getGlobalStatuses: () => api.get('/treatments/statuses/global'),
    getAllStatuses: (params) => api.get('/treatments/statuses/all', { params }),
    getStatuses: (treatmentId) => api.get(`/treatments/${treatmentId}/statuses`),
    createGlobalStatus: (data) => api.post('/treatments/statuses/global', data),
    createStatus: (treatmentId, data) => api.post(`/treatments/${treatmentId}/statuses`, data),
    updateStatus: (statusId, data) => api.put(`/treatments/statuses/${statusId}`, data),
    deleteStatus: (statusId) => api.delete(`/treatments/statuses/${statusId}`),
    reorderStatuses: (data) => api.put('/treatments/statuses/reorder', data),
    // Patient Treatments
    getPlans: (params) => api.get('/patient-treatments', { params }),
    getPlan: (id) => api.get(`/patient-treatments/${id}`),
    getStats: (params) => api.get('/patient-treatments/stats', { params }),
    createPlan: (data) => api.post('/patient-treatments', data),
    updatePlan: (id, data) => api.put(`/patient-treatments/${id}`, data),
    changeStatus: (id, data) => api.put(`/patient-treatments/${id}/status`, data),
    completePlan: (id) => api.put(`/patient-treatments/${id}/complete`),
    // Plan-level custom statuses
    getPlanStatuses: (id) => api.get(`/patient-treatments/${id}/statuses`),
    addPlanStatus: (id, data) => api.post(`/patient-treatments/${id}/statuses`, data),
    updatePlanStatus: (id, statusId, data) => api.put(`/patient-treatments/${id}/statuses/${statusId}`, data),
    deletePlanStatus: (id, statusId) => api.delete(`/patient-treatments/${id}/statuses/${statusId}`),
    getStatusHistory: (id) => api.get(`/patient-treatments/${id}/status-history`),
    // Logs
    getLogs: (id) => api.get(`/patient-treatments/${id}/logs`),
    addLog: (id, data) => api.post(`/patient-treatments/${id}/logs`, data),
    // Schedules
    getSchedules: (id) => api.get(`/patient-treatments/${id}/schedules`),
};

// Instruction Template APIs
export const instructionTemplateAPI = {
    getTemplates: (params) => api.get('/instruction-templates', { params }),
    getTemplate: (id) => api.get(`/instruction-templates/${id}`),
    createTemplate: (data) => api.post('/instruction-templates', data),
    updateTemplate: (id, data) => api.put(`/instruction-templates/${id}`, data),
    deleteTemplate: (id) => api.delete(`/instruction-templates/${id}`),
};

// Treatment Calls (Outbound Voice) APIs
export const treatmentCallsAPI = {
    initiate: (data) => api.post('/treatment-calls/initiate', data),
    getQueue: (params) => api.get('/treatment-calls/queue', { params }),
    retry: (id) => api.post(`/treatment-calls/${id}/retry`),
};

// Dashboard Slides APIs
export const dashboardSlidesAPI = {
    getActive: () => api.get('/dashboard-slides'),
};

// Subscription APIs
export const subscriptionAPI = {
    getPlans: () => api.get('/subscription/plans'),
    getCurrent: () => api.get('/subscription/current'),
    validateCoupon: (code, planId, billingCycle) => api.post('/subscription/validate-coupon', { code, plan_id: planId, billing_cycle: billingCycle }),
    subscribe: (data) => api.post('/subscription/subscribe', data),
    verifyPayment: (data) => api.post('/subscription/verify-payment', data),
    cancel: () => api.post('/subscription/cancel'),
    getUsage: () => api.get('/subscription/usage'),
    getAvailableCoupons: (params) => api.get('/subscription/coupons', { params }),
    getCurrency: () => api.get('/subscription/currency'),
};

export default api;
